// Helpers for surfacing per-row failure detail from the bulk endpoints.
//
// All bulk endpoints return `{ succeeded_count, failed_count, results: [
//   { ok, user_id, store_id, reason } ] }`. Every modal toasts the counts,
// but at scale (hundreds/thousands of operations) the count alone tells
// admins nothing about WHY rows failed. These helpers extract the top
// reasons for the toast and dump the full failures for support triage.


/**
 * Build a short, human-readable summary of the most common failure reasons
 * in a bulk-action results array. Returns "" when there are no failures.
 *
 * Example: "Cannot change a Rightwork internal membership (3); No active
 * membership at this store (1)"
 *
 * @param {Array} results — per-row results from the backend payload
 * @param {number} topN — how many distinct reasons to surface (default 3)
 */
export function summarizeFailureReasons(results, topN = 3) {
  if (!Array.isArray(results)) return "";
  const counts = new Map();
  for (const row of results) {
    if (row && row.ok === false && row.reason) {
      counts.set(row.reason, (counts.get(row.reason) || 0) + 1);
    }
  }
  if (counts.size === 0) return "";
  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  const top = sorted.slice(0, topN).map(([reason, n]) => `${reason} (${n})`);
  if (sorted.length > topN) top.push(`+${sorted.length - topN} more`);
  return top.join("; ");
}


/**
 * Log the full failures array so support engineers can grep the browser
 * console (or copy from a screen recording) when an admin reports a bulk
 * action that "didn't work". The toast only shows the top reasons; this
 * preserves every row.
 */
export function logBulkFailures(actionLabel, results) {
  if (!Array.isArray(results)) return;
  const failures = results.filter((r) => r && r.ok === false);
  if (failures.length === 0) return;
  // eslint-disable-next-line no-console
  console.warn(`[${actionLabel}] ${failures.length} failed rows:`, failures);
}
